import { changeResultsSelection } from "../searchFlightsSlice";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useTranslation } from "react-i18next";
import { DirectionTypes } from "../../../types";
import { selectConfig } from "../../config/ConfigSlice";
import { selectVisibleFlightType } from "../../marker/markerSlice";

function SearchFlightsResultsButtons() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const loading = useAppSelector(
    (state) => state.searchFlights.results.loading
  );
  const type = useAppSelector((state) => state.searchFlights.results.type);
  const direction = useAppSelector(
    (state) => state.searchFlights.results.direction
  );
  const outboundDirect = useAppSelector(
    (state) => state.searchFlights.results.outboundDirect
  );
  const outboundConnection = useAppSelector(
    (state) => state.searchFlights.results.outboundConnection
  );
  const returnDirect = useAppSelector(
    (state) => state.searchFlights.results.returnDirect
  );
  const returnConnection = useAppSelector(
    (state) => state.searchFlights.results.returnConnection
  );
  const roundTrip = useAppSelector(
    (state) => state.searchFlights.form.roundTrip
  );
  const nonStopFlights = useAppSelector(
    (state) => state.searchFlights.form.nonStopFlights
  );

  const { mapper } = useAppSelector(selectConfig);
  const visibleFlightType = useAppSelector(selectVisibleFlightType);

  const oneSubTab = useAppSelector(
    (state) => state.config.components.Results.flightFinder.tabs.oneSubTab
  );

  const returnDirectsDisplay = useAppSelector(
    (state) =>
      state.config.components.Results.flightFinder.return.directs.display
  );

  return (
    <>
      {roundTrip && (
        <div
          id="results-direction-tabs"
          className="tabs is-toggle is-toggle-rounded is-fullwidth mb-1"
        >
          <ul role="tablist" aria-label="Results Direction">
            <li
              role="tab"
              aria-selected={direction === DirectionTypes.Outbound}
              tabIndex={0}
              className={
                direction === DirectionTypes.Outbound
                  ? "is-active results-tab-focusable"
                  : "results-tab-focusable"
              }
              style={{ width: "50%" }}
              onKeyDown={(e) => {
                if (loading) return;
                if (
                  e.key === "Enter" ||
                  e.key === " " ||
                  e.key === "Spacebar"
                ) {
                  dispatch(
                    changeResultsSelection(type, DirectionTypes.Outbound)
                  );
                } else if (e.key === "ArrowRight") {
                  document.getElementById("results-return-tab")?.focus();
                }
              }}
              id="results-outbound-tab"
            >
              <a
                tabIndex={-1}
                onClick={() => {
                  if (loading) return;
                  dispatch(
                    changeResultsSelection(type, DirectionTypes.Outbound)
                  );
                }}
              >
                <span>{t("TAB_HEADER_LABEL_OUTBOUND")}</span>
              </a>
            </li>
            <li
              role="tab"
              aria-selected={direction === DirectionTypes.Return}
              tabIndex={0}
              className={
                direction === DirectionTypes.Return
                  ? "is-active results-tab-focusable"
                  : "results-tab-focusable"
              }
              style={{ width: "50%" }}
              onKeyDown={(e) => {
                if (loading) return;
                if (
                  e.key === "Enter" ||
                  e.key === " " ||
                  e.key === "Spacebar"
                ) {
                  dispatch(changeResultsSelection(type, DirectionTypes.Return));
                } else if (e.key === "ArrowLeft") {
                  document.getElementById("results-outbound-tab")?.focus();
                }
              }}
              id="results-return-tab"
            >
              <a
                tabIndex={-1}
                onClick={() => {
                  if (loading) return;
                  dispatch(changeResultsSelection(type, DirectionTypes.Return));
                }}
              >
                <span>{t("TAB_HEADER_LABEL_RETURN")}</span>
              </a>
            </li>
          </ul>
        </div>
      )}
      {oneSubTab ? (
        <div
          id="results-one-tab"
          className="tabs is-toggle is-toggle-rounded is-fullwidth mb-2"
        >
          {t("FLIGHT_OPTIONS")}
        </div>
      ) : (
        <div
          id="results-type-tabs"
          className="tabs is-toggle is-toggle-rounded is-fullwidth mb-2"
        >
          {returnDirectsDisplay && (
            <ul role="tablist" aria-label="Results Type">
              {(!mapper.enableFlightTypeToggle ||
                visibleFlightType === "direct") && (
                <li
                  role="tab"
                  aria-selected={type === "directAirport"}
                  tabIndex={0}
                  className={
                    type === "directAirport"
                      ? "is-active results-tab-focusable"
                      : "results-tab-focusable"
                  }
                  style={{ width: "50%" }}
                  onKeyDown={(e) => {
                    if (loading) return;
                    if (
                      e.key === "Enter" ||
                      e.key === " " ||
                      e.key === "Spacebar"
                    ) {
                      dispatch(
                        changeResultsSelection("directAirport", direction)
                      );
                    } else if (e.key === "ArrowRight") {
                      document
                        .getElementById("results-connection-tab")
                        ?.focus();
                    }
                  }}
                  id="results-direct-tab"
                >
                  <a
                    tabIndex={-1}
                    onClick={() => {
                      if (loading) return;
                      dispatch(
                        changeResultsSelection("directAirport", direction)
                      );
                    }}
                  >
                    {direction === DirectionTypes.Outbound &&
                      outboundDirect.loading && (
                        <progress className="progress" />
                      )}
                    {direction === DirectionTypes.Outbound &&
                      !outboundDirect.loading && (
                        <span>
                          {t("DIRECT")} (
                          {outboundDirect.error
                            ? "!"
                            : outboundDirect.flights.length}
                          )
                        </span>
                      )}
                    {direction !== DirectionTypes.Outbound &&
                      returnDirect.loading && <progress className="progress" />}
                    {direction !== DirectionTypes.Outbound &&
                      !returnDirect.loading && (
                        <span>
                          {t("DIRECT")} (
                          {returnDirect.error
                            ? "!"
                            : returnDirect.flights.length}
                          )
                        </span>
                      )}
                  </a>
                </li>
              )}

              {!nonStopFlights &&
                (!mapper.enableFlightTypeToggle ||
                  visibleFlightType === "connection") && (
                  <li
                    role="tab"
                    aria-selected={type === "connectionAirport"}
                    tabIndex={0}
                    className={
                      type === "connectionAirport"
                        ? "is-active results-tab-focusable"
                        : "results-tab-focusable"
                    }
                    style={{ width: "50%" }}
                    onKeyDown={(e) => {
                      if (loading) return;
                      if (
                        e.key === "Enter" ||
                        e.key === " " ||
                        e.key === "Spacebar"
                      ) {
                        dispatch(
                          changeResultsSelection("connectionAirport", direction)
                        );
                      } else if (e.key === "ArrowLeft") {
                        document.getElementById("results-direct-tab")?.focus();
                      }
                    }}
                    id="results-connection-tab"
                  >
                    <a
                      tabIndex={-1}
                      onClick={() => {
                        if (loading) return;
                        dispatch(
                          changeResultsSelection("connectionAirport", direction)
                        );
                      }}
                    >
                      {direction === DirectionTypes.Outbound &&
                        outboundConnection.loading && (
                          <progress className="progress" />
                        )}
                      {direction === DirectionTypes.Outbound &&
                        !outboundConnection.loading && (
                          <span>
                            {t("CONNECTING")} (
                            {outboundConnection.error
                              ? "!"
                              : outboundConnection.flights.length}
                            )
                          </span>
                        )}
                      {direction !== DirectionTypes.Outbound &&
                        returnConnection.loading && (
                          <progress className="progress" />
                        )}
                      {direction !== DirectionTypes.Outbound &&
                        !returnConnection.loading && (
                          <span>
                            {t("CONNECTING")} (
                            {returnConnection.error
                              ? "!"
                              : returnConnection.flights.length}
                            )
                          </span>
                        )}
                    </a>
                  </li>
                )}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

export default SearchFlightsResultsButtons;
